import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import Header from '@/components/Header/Header';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { ApplicationInformationForm } from '@/components/Organizations/ApplicationInformationForm';
import { ApplicationHandleForm } from '@/components/Organizations/ApplicationHandleForm';
import { DataNotFound } from '@/components/DataNotFound';
import { FormattedMessage, useIntl } from 'react-intl';
import { CenteredColumn } from '@/components/CenteredColumn';
import { OrganizationApplicationDTO } from '@/api/trafikportalen/models';
import {
  useListOrganizationApplications,
  useListOrganizations,
} from '@/api/trafikportalen/trafikportalenAPI';
import { Loading } from '@/components/Loading';
import { DocumentHead } from '@/components/core/DocumentHead';

export const Route = createFileRoute(
  '/$tenantId/_layout/organizations/applications/$applicationId/',
)({
  component: Application,
  beforeLoad: ({ params }) => {
    const hasPermission = checkUserPermissions({
      permission: 'tenant:viewer',
    });
    if (!hasPermission) {
      throw redirect({ to: '/$tenantId', params });
    }
  },
});

function Application() {
  const { tenantId, applicationId } = Route.useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const { data: applications, isLoading } = useListOrganizationApplications();
  const { data: organizations } = useListOrganizations();

  const application: OrganizationApplicationDTO | undefined = useMemo(() => {
    if (!applications) return;

    return applications.find((app) => app.id === applicationId);
  }, [applications, applicationId]);

  const otherApplications = useMemo(() => {
    if (!applications || !application?.id) return [];
    // Filter out the current application, rejected applications, and approved applications
    // (approved applications are already converted to organizations and checked separately)
    const filtered = applications.filter(
      (app) =>
        app.id !== application.id &&
        app.status !== 'rejected' &&
        app.status !== 'approved',
    );
    return filtered;
  }, [applications, application]);

  const duplicateOrgsByName = useMemo(() => {
    if (!application?.name || !organizations) return [];

    const applicationName = application.name.toLowerCase();

    return organizations.filter(
      (org) => org.name.toLowerCase() === applicationName,
    );
  }, [application?.name, organizations]);

  const duplicateAppsByName = useMemo(() => {
    if (!application?.name || !otherApplications.length) return [];

    const applicationName = application.name.toLowerCase();

    return otherApplications.filter(
      (app) => app.name.toLowerCase() === applicationName,
    );
  }, [application?.name, otherApplications]);

  const hasDuplicateOrganization = duplicateOrgsByName.length > 0;

  const hasDuplicates =
    hasDuplicateOrganization || duplicateAppsByName.length > 0;

  const metadataKeys = useMemo(() => {
    const applicationName = application?.name || 'Ansökan';

    return {
      title: `${applicationName} - Ansökan`,
      description: `Ansökningsdetaljer för ${applicationName}`,
    };
  }, [application?.name]);

  if (isLoading) {
    return <Loading />;
  }

  if (!application) {
    return (
      <DataNotFound
        description={formatMessage({ defaultMessage: 'Ansökan hittades inte' })}
        buttonTitle={formatMessage({ defaultMessage: 'Till ansökningar' })}
        onClick={() =>
          navigate({
            to: '/$tenantId/organizations/$view',
            params: { tenantId, view: 'applications' },
          })
        }
      />
    );
  }

  return (
    <div className="overflow-y-auto">
      <DocumentHead
        title={metadataKeys.title}
        description={metadataKeys.description}
      />
      <Header title={application?.name} />
      <CenteredColumn>
        <div className="my-8 flex w-full flex-col gap-8 px-4 md:w-panel-full md:px-0">
          {hasDuplicates && (
            <div className="rounded-md border border-yellow-500 bg-yellow-50 p-4">
              <div className="flex-1">
                <h3 className="font-semibold text-text-950">
                  <FormattedMessage defaultMessage="Varning: Duplicerad organisations­information" />
                </h3>
                <div className="mt-2 space-y-3 text-text-900">
                  {duplicateOrgsByName.length > 0 && (
                    <div>
                      <p className="font-medium">
                        <FormattedMessage
                          defaultMessage="Det finns redan {count, plural, one {en organisation} other {# organisationer}} med samma namn:"
                          values={{
                            count: duplicateOrgsByName.length,
                          }}
                        />
                      </p>
                      <ul className="mt-2 list-inside list-disc space-y-1">
                        {duplicateOrgsByName.map((org) => (
                          <li key={org.id}>
                            {org.name} (
                            <FormattedMessage defaultMessage="Org.nr:" />{' '}
                            {org.organization_number})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {duplicateAppsByName.length > 0 && (
                    <div>
                      <p className="font-medium">
                        <FormattedMessage
                          defaultMessage="Det finns redan {count, plural, one {en pågående ansökan} other {# pågående ansökningar}} med samma namn:"
                          values={{
                            count: duplicateAppsByName.length,
                          }}
                        />
                      </p>
                      <ul className="mt-2 list-inside list-disc space-y-1">
                        {duplicateAppsByName.map((app) => (
                          <li key={app.id}>
                            {app.name} (
                            <FormattedMessage defaultMessage="Org.nr:" />{' '}
                            {app.organization_number})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <ApplicationInformationForm application={application} />
          {application?.status !== 'rejected' && (
            <ApplicationHandleForm
              application={application}
              hasDuplicateOrganization={hasDuplicateOrganization}
            />
          )}
        </div>
      </CenteredColumn>
    </div>
  );
}
